import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import styled from 'styled-components';
// Lab configuration with colors for labs without seal images
const LAB_CONFIG = {
    'GIA': { color: '#1a365d', fullName: 'Gemological Institute of America' },
    'AGS': { color: '#2d3748', fullName: 'American Gem Society' },
    'IGI': { color: '#553c9a', fullName: 'International Gemological Institute' },
    'HRD': { color: '#c53030', fullName: 'Hoge Raad voor Diamant' },
    'EGL': { color: '#2b6cb0', fullName: 'European Gemological Laboratory' },
    'EGLUSA': { color: '#2b6cb0', fullName: 'EGL USA' },
    'EGLLA': { color: '#2b6cb0', fullName: 'EGL Los Angeles' },
    'GCAL': { color: '#38a169', fullName: 'Gem Certification & Assurance Lab' },
    'GSI': { color: '#dd6b20', fullName: 'Gemological Science International' },
};
// Labs that have seal images available (lowercase for comparison)
const LABS_WITH_SEALS = ['gia', 'ags', 'igi', 'hrd', 'egl'];
// Mapping from lab code to actual seal filename (when they differ)
const LAB_SEAL_FILENAME = {
    'egl': 'egi', // EGL uses egi.webp filename
};
const LabFilterContainer = styled.div `
  margin-bottom: 20px;
`;
const FilterSectionTitle = styled.h4 `
  color: var(--text-secondary);
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 10px;
  margin-top: 0;
`;
const LabBadgeGrid = styled.div `
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(70px, 1fr));
  gap: 8px;
`;
const LabSeal = styled.div `
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 4px;
  transition: all 0.2s ease;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    transition: all 0.2s ease;
  }
`;
const ComingSoonBadge = styled.div `
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-tertiary);
  border: 1px dashed var(--border-color);
  color: var(--text-tertiary);
  font-size: 7px;
  font-weight: 500;
  text-align: center;
  line-height: 1.2;
  margin-bottom: 4px;
  transition: all 0.2s ease;
`;
const LabPill = styled.button `
  padding: 8px 12px;
  min-width: 70px;
  background: ${props => props.$disabled ? 'var(--bg-tertiary)' :
    props.$isActive ? 'var(--accent-gold-bg, rgba(153, 101, 21, 0.08))' : 'var(--bg-secondary)'};
  border: 1px solid ${props => props.$disabled ? 'var(--border-color)' :
    props.$isActive ? 'var(--accent-gold, #996515)' : 'var(--border-color)'};
  color: ${props => props.$isActive ? 'var(--accent-gold, #996515)' : 'var(--text-secondary)'};
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  font-size: 12px;
  font-weight: 500;
  opacity: ${props => props.$disabled ? 0.4 : 1};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;

  &:hover {
    border-color: ${props => props.$disabled ? 'var(--border-color)' : 'var(--accent-gold-border, rgba(153, 101, 21, 0.25))'};
    transform: ${props => props.$disabled ? 'none' : 'translateY(-2px)'};
    box-shadow: ${props => props.$disabled ? 'none' : '0 4px 8px rgba(0,0,0,0.1)'};
  }

  &:hover ${LabSeal} img {
    transform: scale(1.1);
  }

  &:hover ${ComingSoonBadge} {
    border-color: var(--accent-gold-border, rgba(153, 101, 21, 0.25));
  }

  .lab-name {
    font-weight: 600;
    font-size: 11px;
  }

  .lab-count {
    font-size: 10px;
    color: var(--text-tertiary);
  }
`;
const AllLabsPill = styled(LabPill) `
  background: ${props => props.$isActive ? 'var(--accent-gold, #996515)' : 'var(--bg-secondary)'};
  color: ${props => props.$isActive ? '#fff' : 'var(--text-primary)'};
  font-weight: 600;

  .lab-name {
    color: ${props => props.$isActive ? '#fff' : 'var(--text-primary)'};
  }

  .lab-count {
    color: ${props => props.$isActive ? 'rgba(255,255,255,0.8)' : 'var(--text-tertiary)'};
  }
`;
// Individual lab seal component with error handling
const LabSealImage = ({ lab, sealImageBase, hasSeal }) => {
    const [imageError, setImageError] = useState(false);
    const labLower = lab.toLowerCase();
    // Use mapped filename if available, otherwise use lab code
    const sealFilename = LAB_SEAL_FILENAME[labLower] || labLower;
    if (!hasSeal || imageError) {
        return (_jsxs(ComingSoonBadge, { children: ["SEAL", _jsx("br", {}), "COMING", _jsx("br", {}), "SOON"] }));
    }
    return (_jsx(LabSeal, { children: _jsx("img", { src: `${sealImageBase}/${sealFilename}.webp`, alt: `${lab} certification seal`, onError: () => setImageError(true) }) }));
};
export const LabFilter = ({ selectedLab, labs, onLabChange, getLabCount, availableLabSeals = LABS_WITH_SEALS, sealImageBase = 'https://blobaneesh.blob.core.windows.net/images/seal' }) => {
    const totalCount = labs.reduce((sum, lab) => sum + getLabCount(lab), 0);
    return (_jsxs(LabFilterContainer, { children: [_jsx(FilterSectionTitle, { children: "Certification Labs" }), _jsxs(LabBadgeGrid, { children: [_jsxs(AllLabsPill, { "$isActive": !selectedLab, onClick: () => onLabChange(''), title: `All Labs (${totalCount} diamonds)`, children: [_jsx("span", { className: "lab-name", children: "All" }), _jsx("span", { className: "lab-count", children: totalCount })] }), labs.map(lab => {
                        const count = getLabCount(lab);
                        const isDisabled = count === 0;
                        const config = LAB_CONFIG[lab] || LAB_CONFIG[lab.toUpperCase()];
                        const labLower = lab.toLowerCase();
                        const hasSeal = availableLabSeals.includes(labLower);
                        return (_jsxs(LabPill, { "$isActive": selectedLab === lab, "$disabled": isDisabled, onClick: () => !isDisabled && onLabChange(lab), title: `${config?.fullName || lab} (${count} diamonds)`, children: [_jsx(LabSealImage, { lab: lab, sealImageBase: sealImageBase, hasSeal: hasSeal }), _jsx("span", { className: "lab-name", children: lab }), _jsx("span", { className: "lab-count", children: count })] }, lab));
                    })] })] }));
};
